import ShareSpacePanelAtom from "utilities/atoms/bookview/share-space-panel-atom";
import ShareSpaceRecipientRecord from "models/view-models/share-spaces/share-space-recipient-record";
import ShareSpaceRecipientService from "utilities/services/share-space/share-space-recipients-service";
import ShareSpaceService, {
    SendShareSpaceServiceParams,
    ShareSpaceResourcePathParams,
} from "utilities/services/share-space/share-space-service";
import StringUtils from "utilities/string-utils";
import useBuildShareSpacePanel from "./share-space-panel/use-build-share-space-panel";
import { CollectionUtils } from "@rsm-hcd/javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { siteMap } from "internal-sitemap";
import { useAtom } from "jotai";
import { useLocalization } from "./use-localization";

const DEFAULT_SHARE_SPACE_TEMPLATE = `${window.location.origin}${siteMap.shareSpace.share}`;

const useShareFormSubmit = (
    setLoading: (loading: boolean) => void,
    setValidationErrors: (errors: string) => void
) => {
    const ShareSpaceErrorKeys = {
        General: "General",
    };
    const { t } = useLocalization();
    const [sharePanelAtom] = useAtom(ShareSpacePanelAtom);
    const [shareSpacePanel] = useAtom(ShareSpacePanelAtom);
    const { shareSpace, emails, message } = shareSpacePanel;
    const { patch: sendApi } = ShareSpaceService.usePatchSend();
    const { loadShareSpace } = useBuildShareSpacePanel();

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            if (CollectionUtils.isEmpty(sharePanelAtom.emails)) {
                ToastManager.error(t("email-required-field"));
                setLoading(false);
                return;
            }
            if (
                sharePanelAtom.emails?.some(
                    (e: string) => !StringUtils.isValidEmail(e)
                )
            ) {
                setValidationErrors(
                    t("validation-multipleInvalid", {
                        type: t("field-email_plural"),
                    })
                );
                setLoading(false);
                return;
            }
            handleSendShare();
            ToastManager.success(t("shareItems__success"));
        } catch (error) {}
    };

    const handleSendShare = async () => {
        try {
            if (shareSpace.id == null || shareSpace.id <= 0) {
                throw new Error(ShareSpaceErrorKeys.General);
            }

            const createResponse = await ShareSpaceRecipientService.bulkCreate(
                emails.map(
                    (email) =>
                        new ShareSpaceRecipientRecord({
                            email,
                            shareSpaceId: shareSpace.id!,
                        })
                )
            );

            if (createResponse.result?.hasErrors()) {
                throw new Error(ShareSpaceErrorKeys.General);
            }

            const sendPatchPatchPathParams: ShareSpaceResourcePathParams = {
                id: shareSpace.id,
            };

            const sendShareSpaceServiceParams: SendShareSpaceServiceParams = {
                id: shareSpace.id,
                message: message,
                magicLinkUrlTemplate: DEFAULT_SHARE_SPACE_TEMPLATE,
            };

            const sendResponse = await sendApi(
                sendPatchPatchPathParams,
                sendShareSpaceServiceParams
            );

            if (sendResponse.result?.hasErrors()) {
                throw new Error(ShareSpaceErrorKeys.General);
            }

            // reload the share panel
            await loadShareSpace();
        } catch {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("sending"),
                    resource: t("share"),
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return { handleFormSubmit, ShareSpaceErrorKeys };
};

export default useShareFormSubmit;
